import * as escapeStringRegexp from 'escape-string-regexp';
import { assign, fromPairs, map } from 'lodash';

import { stringSort } from '../sorter';
import * as axelUtils from '../util';
import { straightTree } from './straight/straight-tree';

export interface StraightDataOpts {
  data?: any;
  sorter?: any;
  filter?: any;
  formatter?: any;
  filterColumns?: any;
  columns?: any;
  value?: any;
  rowLimit?: number;
  memoize?: boolean;
}

export interface Sortable {
  data: any;
  rowKey: string;
}

/**
 * Straight Model
 *
 * A meta sortable and filterable collection data object.  Data is
 * aggregated into a {straightTree} that allows you to supply getters
 * and formatted getters to tree members
 * @see {straightTree}
 * @example
 *    let data = [{a: 1}, {a: 2, b:3}, {c: 4}];
 *    let opts = {
 *      value: function(data, column) {
 *        return function() {
 *          return data ? data : 'Value Not Defined';
 *        }
 *      }
 *    };
 *    let model = new StraightModel(data, opts);
 *    let cols = model.cols()
 *    //-> ['a', 'b', 'c']
 *    let tree = model.tree()
 *    //-> {
 *    //->   0: {a: Object, b: Object, c: Object}
 *    //->   1: {a: Object, b: Object, c: Object}
 *    //->   2: {a: Object, b: Object, c: Object}
 *    //-> }
 *
 *    // pull value 'a' from the first record (at index '0')
 *    tree[0]['a'].value()
 *    //-> 1
 *
 *    // pull undefined value 'b' from the first record ( at index '0')
 *    tree[0]['b'].value()
 *    //-> 'Value Not Defined'
 *
 * @todo rename private attributes away from underscore prefix
 * @todo optimize for performance
 */
export class StraightModel {
  private __sortState: { colIndex: number; order: string };
  private _records: Array<any>;
  private _tree: any;
  private _cols: Array<string>;
  private _rows: Array<string>;
  private _settings: StraightDataOpts;

  constructor(records, opts: StraightDataOpts = {}) {
    const defaults = {
      columns: axelUtils.collectionKeys(records),
      filter: () => true,
      filterColumns: null,
      rowLimit: records.length,
      sorter: stringSort,
      value: null,
      formatter: null,
      memoize: false,
    };

    this.__sortState = { colIndex: null, order: null };
    this._settings = assign({}, defaults, opts);
    this._records = records;
    this.init();
  }

  init() {
    const { value, formatter, rowLimit, memoize } = this.settings;

    this.initCols();
    this._tree = straightTree(this.records, this.cols(), { value, formatter, rowLimit, memoize });
    this._rows = this.buildRows();
  }

  get settings(): StraightDataOpts {
    return this._settings;
  }

  initCols() {
    const { filterColumns, columns } = this.settings;

    // filter columns
    if (filterColumns) {
      this._cols = filterColumns.call(null, columns);
    } else {
      this._cols = columns;
    }
  }

  buildRows(limit: number = null) {
    const { rowLimit } = this.settings;
    const tree = this._tree;

    return Object.keys(tree).slice(0, limit || rowLimit);
  }

  get records() {
    return this._records;
  }

  get tree() {
    return this._tree;
  }

  cols() {
    return this._cols;
  }

  rows() {
    return this._rows;
  }

  selectColumn(colKeys: Array<string>): { [colKey: string]: Array<any> } {
    return fromPairs(colKeys.map((colKey) => [colKey, map(this.records.slice(0, this.rowLimit()), colKey)]));
  }

  sorter() {
    return this._settings.sorter;
  }

  sorted(colIndex, order) {
    const sortState = this._sortState();

    return colIndex === sortState.colIndex && order === sortState.order;
  }

  _sortState() {
    return this.__sortState;
  }

  filter() {
    return this._settings.filter();
  }

  rowLimit() {
    return this._settings.rowLimit;
  }

  filterRows(filterQuery, caseSensitive = false) {
    const tree = this.tree;
    const cols = this.cols();
    const flag = caseSensitive ? '' : 'i';
    const pattern = new RegExp(escapeStringRegexp(filterQuery.toString()), flag);
    const rowContains = (rowKey, pattern) => {
      const separator = ':|:';
      const row = tree[rowKey];
      const addRowValue = (accu, colKey) => accu + separator + row[colKey].format();

      return pattern.test(cols.reduce(addRowValue, ''));
    };

    this._rows = this._rows.filter(function containsQuery(rowKey) {
      return rowContains(rowKey, pattern);
    });
  }

  sort(colIndex, order) {
    const tree = this.tree;
    const cols = this.cols();
    const { sorter } = this.settings;
    const colKey = cols[colIndex];
    const sortWithColKey = sorter.bind(null, colKey, order);
    const getRowKey = (s: Sortable) => s.rowKey;
    const getSortable = (rowKey): Sortable => ({
      data: tree[rowKey][colKey].value(),
      rowKey: rowKey,
    });
    const orderedSort = (a: [Sortable, number], b: [Sortable, number]) => {
      if (/asc/i.test(order)) {
        var result = sortWithColKey(a[0], b[0]);
      } else {
        var result = sortWithColKey(b[0], a[0]);
      }
      return result ? result : a[1] - b[1];
    };

    if (colKey && order) {
      if (!this.sorted(colIndex, order)) {
        this._rows = this._rows
          .map(getSortable)
          // add second key for stable sorting
          // @relatedto https://app.asana.com/0/0/341086152961155
          .map((x, i) => [x, i])
          .sort(orderedSort)
          .map(([x, i]) => x)
          .map(getRowKey);

        // update sort state
        assign(this.__sortState, { colIndex, order });
      }
    }

    return this;
  }

  /**
   * To Collection
   *
   * @todo seems pointless for a one dimensional records array, consider removing
   * @returns {Array}
   */
  toCollection() {
    const records: Array<any> = this.records;
    const rows: Array<string> = this.rows();

    // generate a collection of records from sorted array of rowKeys
    return rows.reduce((accu, rowKey) => accu.push(records[rowKey]) && accu, []);
  }
}

export default function straight(records, opts: StraightDataOpts = {}) {
  return new StraightModel(records, opts);
}
